import OrderDTO from "@/dto/order/OrderDTO";
import { autoPrintSupportShopTypes, defaultOmitPrintOrderErrorValidations } from "./constant";
import ShopType from "@/enum/ShopType";
import MultiShopSelectStore from "@/components/MultiShopSelect/MultiShopSelectStore";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import { AutoPrintTaskStatus, AutoPrintTaskType, getRunningAutoPrintTask } from "@/services/api/autoPrint";
import ShopDTO from "@/dto/shop/ShopDTO";
import { Modal } from "antd";
import { confirm } from "@/utils";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import {
    checkAppointmentArrivalOrderTemplateRule,
    checkJdConsolidationOrderTemplateRule,
    checkShipBySfOrderTemplateRule,
} from "@/utils/WaybillTemplateRule";
import OrderTag from "@/enum/OrderTag";
import { isAnyOrderWithDDSupplierTag, isAnyOrderWithTbCanNotShipTag } from "@/utils/OrderTagUtils";

/**
 * 获取自动打印默认忽略订单的相关错误
 */
export async function getOmitAutoPrintOrderErrorMessage(order: OrderDTO) {
    for (let j = 0; j < defaultOmitPrintOrderErrorValidations.length; j++) {
        const { validator, message } = defaultOmitPrintOrderErrorValidations[j];

        const error = await validator(order);

        if (error) {
            return message;
        }
    }

    return null;
}

export function getMultiShopSelectPlatformType(shopType: ShopType) {
    if (MultiShopSelectStore.tbShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.tb;
    } else if (MultiShopSelectStore.pddShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.pdd;
    } else if (MultiShopSelectStore.dyShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.dy;
    } else if (MultiShopSelectStore.jdShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.jd;
    } else if (MultiShopSelectStore.ksShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.ks;
    } else if (MultiShopSelectStore.aliShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.ali;
    } else if (MultiShopSelectStore.xhsShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.xhs;
    } else if (MultiShopSelectStore.channelShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.channelShop;
    } else if (MultiShopSelectStore.wbShopTypes.includes(shopType)) {
        return MultiShopSelectPlatformType.wb;
    } else {
        return MultiShopSelectPlatformType.other;
    }
}

/**
 * 批量查询支持自动打印订单的店铺类型
 */
export function hasAutoPrintSupportShopTypes(shopTypes: ShopType[]) {
    for (let i = 0; i < shopTypes.length; i++) {
        const shopType = shopTypes[i];

        const flag = autoPrintSupportShopTypes.includes(shopType);

        if (flag) {
            return true;
        }
    }

    return false;
}

async function checkRunningAutoPrintTask(shopIds: number[], type: AutoPrintTaskType) {
    try {
        const results = await getRunningAutoPrintTask(shopIds);

        const result = results.find((it) => it.status === AutoPrintTaskStatus.RUNNING && it.type === type);

        if (result?.status === AutoPrintTaskStatus.RUNNING) {
            return {
                status: true,
                shopIds: result?.shopIds,
            };
        }
    } catch (error) {}

    return {
        status: false,
    };
}

function getTipMessage(type: AutoPrintTaskType, shopNames: string, showConfirm = false) {
    switch (type) {
        case AutoPrintTaskType.PACKING_LIST: {
            return `店铺${shopNames}正在进行自动打印发货单，${
                showConfirm ? "请谨慎" : "不支持"
            }对该店铺订单进行批量打印发货单操作`;
        }
        case AutoPrintTaskType.WAYBILL:
        default:
            return `店铺${shopNames}正在进行自动打印快递单，${
                showConfirm ? "请谨慎" : "不支持"
            }对该店铺订单进行批量打印快递单，批量发货和导入单号操作`;
    }
}

/**
 * 检查传入的店铺是否开启自动打印功能
 */
export async function checkShopIdsEnabledAutoPrint(
    shops: Pick<ShopDTO, "id" | "name" | "type">[] = [],
    showConfirm = false,
    type = AutoPrintTaskType.WAYBILL,
) {
    const shopIds = shops?.map((shop) => shop?.id)?.filter((it) => !!it);
    const shopTypes = shops?.map((shop) => shop?.type);

    const supportAutoPrint = hasAutoPrintSupportShopTypes(shopTypes);

    if (supportAutoPrint) {
        const result = await checkRunningAutoPrintTask(shopIds, type);

        if (result.status) {
            const openedShopNames = shops.map((shop) => shop?.name)?.join?.(",");
            const tipMessage = getTipMessage(type, openedShopNames, showConfirm);

            if (showConfirm) {
                try {
                    await confirm(tipMessage);

                    return true;
                } catch (error) {
                    return false;
                }
            } else {
                Modal.error({
                    title: tipMessage,
                    content: "请先关闭对应店铺的自动打印，再进行操作。",
                });

                return false;
            }
        }
    }

    return true;
}

export async function checkOrderWaybillNoPrintable(order: OrderDTO, waybillTemplate: WaybillTemplateDTO) {
    const shipBySfErrorMessage = checkShipBySfOrderTemplateRule([order], waybillTemplate);

    if (shipBySfErrorMessage) return shipBySfErrorMessage;

    const appointmentArrivalErrorMessage = checkAppointmentArrivalOrderTemplateRule([order], waybillTemplate);

    if (appointmentArrivalErrorMessage) return appointmentArrivalErrorMessage;

    const jdConsolidationMessage = checkJdConsolidationOrderTemplateRule([order], waybillTemplate);

    if (jdConsolidationMessage) return jdConsolidationMessage;

    /** 周期购直接不给打印 */

    if (order.doudianOrderExtraInfo?.periodPurchaseInfoList?.length && order.tags?.includes(OrderTag.PERIOD_PURCHASE)) {
        return "周期购订单，每次只能发货当期商品，无法自动打印快递单。";
    }

    // if (isAnyOrderWithAliCanNotShipTag([order])) {
    //     return "阿里不能发货订单，无法自动打印快递单。";
    // }

    if (isAnyOrderWithTbCanNotShipTag([order])) {
        return "淘宝不能发货订单，无法自动打印快递单。";
    }

    if (isAnyOrderWithDDSupplierTag([order])) {
        return "抖店供应链订单，无法自动打印快递单。";
    }
}
